import Vue from "vue";

import axios, { AxiosInstance } from "axios";
import { Auth } from "../auth";

export const apiStats = Vue.observable({
  pendingRequests: 0,
  totalRequests: 0,
  failedRequests: 0,
  lastRequestTime: new Date(),
});

export class Api {
  private axios: AxiosInstance;

  constructor(private baseUrl = "") {
    this.initBaseUrl(baseUrl);

    this.axios = axios.create();

    // FIRST Interceptor
    // the request interceptor will be the last request interceptor executed
    //             (after all other request interceptors and just before performing the request)
    // the response interceptor will be the first response interceptor executed
    //             (before all other response interceptor and just after performing the request)

    this.axios.interceptors.request.use(async config => {
      await Auth.ensureAuthenticated();

      config.headers = config.headers || {};
      if (!(config as any).noAuth) {
        config.headers["X-jti"] = Auth.claims!.jti;
      }
      return config;
    });

    this.axios.interceptors.response.use(
      response => {
        return response;
      },
      async error => {
        if (
          error?.response?.status === 401 &&
          error.config &&
          (await Auth.ensureAuthenticated(Auth.calledOnError401))
        ) {
          // use axios default library instance (like done in Auth.ensureAuthenticated)
          // to reexecute the request - it will ensure that no interceptor will be used for this request
          return axios.request(error.config);
        }
        return Promise.reject(error);
      }
    );

    // LAST Interceptor
    // the request interceptor will be the first request interceptor executed
    //             (before all other request interceptors and before performing the request)
    // the response interceptor will be the last response interceptor executed
    //             (after all other response interceptor and after performing the request)
    this.axios.interceptors.request.use(
      config => {
        apiStats.pendingRequests++;
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    this.axios.interceptors.response.use(
      response => {
        apiStats.pendingRequests--;
        apiStats.totalRequests++;
        apiStats.lastRequestTime = new Date();
        return response;
      },
      async error => {
        apiStats.pendingRequests--;
        apiStats.totalRequests++;
        apiStats.failedRequests++;
        apiStats.lastRequestTime = new Date();
        if (error.response?.status === 401) {
          await Auth.Force401RefreshToken();
          return await axios.create().request(error.config);
        }
        return Promise.reject(error);
      }
    );
  }

  public get<T = any>(url: string, query?: any, headers?: any, config?: any) {
    return this.axios.get<T>(this.getFullUrl(url), Object.assign({}, { params: query, headers }, config));
  }
  public delete<T = any>(url: string, query?: any, headers?: any, config?: any) {
    return this.axios.delete<T>(this.getFullUrl(url), Object.assign({}, { params: query, headers }, config));
  }
  public head(url: string, query?: any, headers?: any) {
    return this.axios.head(this.getFullUrl(url), { headers });
  }
  public post<T = any>(url: string, data?: any, headers?: any, query?: any, config?: any) {
    return this.axios.post<T>(this.getFullUrl(url), data, Object.assign({}, { params: query, headers }, config));
  }
  public put<T = any>(url: string, data?: any, headers?: any) {
    return this.axios.put<T>(this.getFullUrl(url), data, { headers });
  }
  public patch<T = any>(url: string, data?: any, headers?: any) {
    return this.axios.patch<T>(this.getFullUrl(url), data, { headers });
  }

  private getFullUrl(url: string): string {
    if (url.match(/^https?:/) || url[0] == "/") {
      return url;
    }

    return this.baseUrl + url;
  }

  private initBaseUrl(apiBaseUrl: string) {
    this.baseUrl = process.env.BASE_URL || "/";
    if (!this.baseUrl.endsWith("/")) {
      this.baseUrl += "/";
    }
    this.baseUrl += "api/";

    if (apiBaseUrl) {
      this.baseUrl = this.getFullUrl(
        `${apiBaseUrl
          .split("/")
          .filter(p => p)
          .join("/")}/`
      );
    }
  }
}
